'use client';

import { useEffect, useState } from 'react';

import {
  useCreateDeliverableTypeMutation,
  useUpdateDeliverableTypeMutation,
} from '@/features/deliverable-types/api/deliverable-typesApi';
import { createDeliverableTypeSchema } from '@/features/deliverable-types/schemas';
import { useCanWorkspaceAction } from '@/features/workspaces/hooks';
import { MainButton, SecondaryButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './DeliverableTypeModalForm.module.scss';

import type { DeliverableType } from '@/features/deliverable-types/types';

type DeliverableTypeModalFormMode = 'create' | 'edit';

type DeliverableTypeModalFormProps = {
  mode: DeliverableTypeModalFormMode;
  deliverableType?: DeliverableType | null;
  onCancel?: () => void;
  onSuccess?: () => void;
};

function toLabel(deliverableType?: DeliverableType | null) {
  return deliverableType?.label ?? '';
}

export function DeliverableTypeModalForm({
  mode,
  deliverableType,
  onCancel,
  onSuccess,
}: DeliverableTypeModalFormProps) {
  const [label, setLabel] = useState(() => toLabel(deliverableType));
  const canCreateDeliverableTypes = useCanWorkspaceAction(
    'deliverable_type_management',
    'create',
  );
  const canEditDeliverableTypes = useCanWorkspaceAction(
    'deliverable_type_management',
    'edit',
  );
  const [createDeliverableType, { isLoading: isCreating }] =
    useCreateDeliverableTypeMutation();
  const [updateDeliverableType, { isLoading: isUpdating }] =
    useUpdateDeliverableTypeMutation();

  useEffect(() => {
    setLabel(toLabel(deliverableType));
  }, [deliverableType]);

  const isValid =
    createDeliverableTypeSchema.shape.label.safeParse(label).success;
  const isLoading = isCreating || isUpdating;
  const submitLabel = mode === 'create' ? 'Create' : 'Update';
  const canSubmit =
    mode === 'create' ? canCreateDeliverableTypes : canEditDeliverableTypes;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      notify({
        type: 'error',
        message: 'You do not have permission to manage deliverable types.',
      });
      return;
    }

    if (!isValid || isLoading) {
      return;
    }

    try {
      if (mode === 'create') {
        await createDeliverableType({ label: label.trim() }).unwrap();
        notify({ type: 'success', message: 'Deliverable type created.' });
      } else if (deliverableType) {
        await updateDeliverableType({
          deliverableTypeId: deliverableType.id,
          payload: { label: label.trim() },
        }).unwrap();
        notify({ type: 'success', message: 'Deliverable type updated.' });
      }

      onSuccess?.();
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <form className={styles.deliverableTypeModalForm} onSubmit={handleSubmit}>
      <SmartInput
        type="text"
        name="deliverable-type-name"
        label="Name"
        value={label}
        onChange={setLabel}
        schema={createDeliverableTypeSchema.shape.label}
      />

      <div className={styles.deliverableTypeModalForm__actions}>
        <SecondaryButton
          ariaLabel="Cancel deliverable type form"
          onClick={onCancel}
        >
          Cancel
        </SecondaryButton>
        <MainButton
          type="submit"
          ariaLabel={submitLabel}
          isLoading={isLoading}
          disabled={!isValid || !canSubmit}
        >
          {submitLabel}
        </MainButton>
      </div>
    </form>
  );
}
